import React, { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, FileDown, UploadCloud, ChevronDown, ChevronUp } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  downloadsContentSchema,
  type DownloadsContent,
  type DownloadItem,
  type DownloadCategory,
} from "@profolio/validators";

interface DownloadsEditorProps {
  content: Record<string, unknown>;
  onChange: (content: DownloadsContent) => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export function DownloadsEditor({ content, onChange }: DownloadsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  const getUploadUrlMutation = trpc.media.getUploadUrl.useMutation();
  const confirmUploadMutation = trpc.media.confirmUpload.useMutation();

  const parseResult = downloadsContentSchema.safeParse(content);
  const data: DownloadsContent = parseResult.success
    ? parseResult.data
    : { heading: "Downloads & Resources", items: [] };

  const heading = data.heading || "Downloads & Resources";
  const items: DownloadItem[] = data.items || [];

  const notifyChange = (newHeading: string, newItems: DownloadItem[]) => {
    onChange({
      heading: newHeading,
      items: newItems,
    });
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    notifyChange(e.target.value, items);
  };

  const addItem = () => {
    if (items.length >= 50) return;
    const newItem: DownloadItem = {
      id: crypto.randomUUID(),
      title: "",
      category: "lecture_notes",
      description: "",
      fileUrl: "",
      fileName: "",
      fileSize: "",
      fileFormat: "PDF",
      publishDate: new Date().toISOString().split("T")[0],
    };
    const updated = [newItem, ...items];
    setExpandedIndex(0);
    notifyChange(heading, updated);
  };

  const updateItem = (index: number, patch: Partial<DownloadItem>) => {
    const updated = [...items];
    updated[index] = { ...updated[index], ...patch };
    notifyChange(heading, updated);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    if (expandedIndex === index) setExpandedIndex(null);
    notifyChange(heading, updated);
  };

  const handleFileUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File size must be under 25MB");
      return;
    }

    const extension = file.name.split(".").pop()?.toUpperCase() || "FILE";
    const sizeStr = formatBytes(file.size);

    try {
      setUploadingIndex(index);
      toast.info("Uploading document to Cloudflare R2...");

      const { url, objectKey } = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
        fileSize: file.size,
        category: "document",
      });

      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload file to Cloudflare R2");
      }

      const confirmRes = await confirmUploadMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
        fileSize: file.size,
        category: "document",
        objectKey,
      });

      updateItem(index, {
        fileUrl: confirmRes.media.r2Url,
        fileName: file.name,
        fileSize: sizeStr,
        fileFormat: extension,
        title: items[index].title || file.name.replace(/\.[^/.]+$/, ""),
      });
      toast.success("File uploaded to R2 successfully");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to upload document";
      toast.error(msg);
    } finally {
      setUploadingIndex(null);
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index]!.value = "";
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="downloads-heading" className="text-xs font-semibold">
          Section Heading
        </Label>
        <Input
          id="downloads-heading"
          value={heading}
          onChange={handleHeadingChange}
          placeholder="Downloads & Resources"
          className="mt-1 text-xs"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Downloadable Resources ({items.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Provide student slides, lecture notes, datasets, code samples, or paper preprints.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={addItem}
            disabled={items.length >= 50}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Resource
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
            <FileDown className="mx-auto h-8 w-8 text-muted-foreground/60 mb-2" />
            <p className="text-sm font-medium">No downloads added yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm">
              Click "Add Resource" to upload course slides, datasets, or paper PDFs.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              className="mt-4 gap-1.5 text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Add First Resource
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <div
                  key={item.id || index}
                  className="rounded-lg border bg-card p-3.5 transition-all shadow-2xs hover:border-primary/30"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className="flex-1 cursor-pointer font-medium text-xs flex items-center gap-2 min-w-0"
                      onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    >
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                        {item.fileFormat || "FILE"}
                      </span>
                      <span className="truncate text-foreground font-semibold">
                        {item.title || "Untitled Resource"}
                      </span>
                      {item.fileSize && (
                        <span className="text-[11px] text-muted-foreground">({item.fileSize})</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        className="h-7 w-7 text-muted-foreground"
                      >
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3.5 space-y-3 pt-3.5 border-t">
                      {/* 1. Essential Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <Label className="text-xs font-medium">Resource Title *</Label>
                          <Input
                            placeholder="e.g. Lecture 01: Introduction to Distributed Systems"
                            value={item.title}
                            onChange={(e) => updateItem(index, { title: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Category</Label>
                          <select
                            value={item.category}
                            onChange={(e) => updateItem(index, { category: e.target.value as DownloadCategory })}
                            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm"
                          >
                            <option value="lecture_notes" className="bg-card text-foreground">Lecture Notes</option>
                            <option value="slides" className="bg-card text-foreground">Presentation Slides</option>
                            <option value="syllabus" className="bg-card text-foreground">Syllabus / Guide</option>
                            <option value="dataset" className="bg-card text-foreground">Research Dataset</option>
                            <option value="code_sample" className="bg-card text-foreground">Code Sample / Script</option>
                            <option value="other" className="bg-card text-foreground">Other Document</option>
                          </select>
                        </div>
                      </div>

                      {/* Direct Upload to Cloudflare R2 */}
                      <div className="rounded-md border border-dashed p-3 bg-muted/20">
                        <input
                          type="file"
                          ref={(el) => {
                            fileInputRefs.current[index] = el;
                          }}
                          className="hidden"
                          onChange={(e) => handleFileUpload(index, e)}
                        />
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs">
                            <span className="font-semibold text-foreground">Upload file directly to R2</span>
                            <p className="text-[11px] text-muted-foreground">
                              Supported: PDF, PPTX, DOCX, ZIP, CSV, TXT (Max 25MB).
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => fileInputRefs.current[index]?.click()}
                            disabled={uploadingIndex === index}
                            className="gap-1 text-xs shrink-0 h-7"
                          >
                            <UploadCloud className="h-3.5 w-3.5" />
                            {uploadingIndex === index ? "Uploading..." : "Choose File"}
                          </Button>
                        </div>
                      </div>

                      {/* 2. Progressive Disclosure: Direct Link, Meta & Description */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <Label className="text-xs font-medium">Download URL (auto-filled on upload)</Label>
                          <Input
                            placeholder="https://..."
                            value={item.fileUrl}
                            onChange={(e) => updateItem(index, { fileUrl: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">File Format (e.g. PDF, ZIP)</Label>
                          <Input
                            placeholder="PDF"
                            value={item.fileFormat || ""}
                            onChange={(e) => updateItem(index, { fileFormat: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium">File Size (e.g. 4.2 MB)</Label>
                          <Input
                            placeholder="e.g. 4.2 MB"
                            value={item.fileSize || ""}
                            onChange={(e) => updateItem(index, { fileSize: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Release Date</Label>
                          <Input
                            placeholder="e.g. 2025-09-01"
                            value={item.publishDate || ""}
                            onChange={(e) => updateItem(index, { publishDate: e.target.value })}
                            className="mt-1 text-xs"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Description & Notes</Label>
                        <Textarea
                          placeholder="Brief description of the document contents, license, or course week..."
                          value={item.description || ""}
                          onChange={(e) => updateItem(index, { description: e.target.value })}
                          rows={2}
                          className="mt-1 text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
